import { useEffect, useState, type ReactNode } from 'react';
import { Check, Loader2, LogOut, Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import { Header } from '@/components/Header';
import { useAuth } from '@/contexts/AuthContext';
import { useProject } from '@/contexts/ProjectProvider';
import { useOrgStore } from '@/store/org';
import { readTheme, saveTheme, type Theme } from '@/lib/theme';
import { APP_VERSION, BUILD_STAMP } from '@/lib/buildInfo';
import { getBundleVersion } from '@/lib/liveUpdate';
import { platformName } from '@/lib/platform';

const ТЕМЫ: Array<{ key: Theme; label: string; icon: LucideIcon }> = [
  { key: 'system', label: 'Как в системе', icon: Monitor },
  { key: 'light', label: 'Светлая', icon: Sun },
  { key: 'dark', label: 'Тёмная', icon: Moon },
];

function Секция({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="px-4 pt-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-faint px-1 pb-2">{title}</h2>
      <div className="card overflow-hidden">{children}</div>
    </section>
  );
}

export default function MorePage() {
  const { user, logout } = useAuth();
  const { projects, projectId, setActive, status } = useProject();
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const switchingTo = useOrgStore((s) => s.switchingTo);
  const switchError = useOrgStore((s) => s.switchError);
  const switchOrg = useOrgStore((s) => s.switchOrg);
  const [тема, setТема] = useState<Theme>(readTheme());
  const [пакет, setПакет] = useState('…');

  useEffect(() => { void getBundleVersion().then(setПакет); }, []);

  const имя = (user?.user_metadata as { full_name?: string } | undefined)?.full_name || user?.email?.split('@')[0] || '—';

  return (
    <>
      <Header title="Ещё" subtitle="Профиль и настройки" />
      <div className="scr pb-6">
        <Секция title="Профиль">
          <div className="p-4">
            <p className="font-semibold text-lg">{имя}</p>
            <p className="text-sm text-muted">{user?.email}</p>
          </div>
        </Секция>

        {/* Организации — только если их больше одной (у большинства одна, и строка была бы лишней). */}
        {orgs.length > 1 && (
          <Секция title="Организация">
            {orgs.map((o) => (
              <button
                key={o.id} type="button" role="radio" aria-checked={o.id === activeOrgId}
                disabled={!!switchingTo}
                onClick={() => void switchOrg(o.id)}
                className="w-full min-h-12 flex items-center gap-3 px-4 py-3 text-left border-b border-line last:border-b-0 active:bg-surface2 disabled:opacity-60"
              >
                <span className="flex-1 font-medium">{o.name}</span>
                {switchingTo === o.id && <Loader2 className="size-5 text-accent animate-spin" aria-label="Переключаем" />}
                {o.id === activeOrgId && !switchingTo && <Check className="size-5 text-accent" aria-label="Выбрана" />}
              </button>
            ))}
            {switchError && <p className="px-4 py-3 text-sm text-danger" role="alert">{switchError}</p>}
          </Секция>
        )}

        <Секция title="Проект">
          {status === 'loading' && <p className="p-4 text-sm text-muted">Загружаем проекты…</p>}
          {status === 'ready' && projects.length === 0 && <p className="p-4 text-sm text-muted">У вас пока нет проектов.</p>}
          {projects.map((p) => (
            <button
              key={p.id} type="button" role="radio" aria-checked={p.id === projectId}
              onClick={() => setActive(p.id)}
              className="w-full min-h-12 flex items-center gap-3 px-4 py-3 text-left border-b border-line last:border-b-0 active:bg-surface2"
            >
              <span className="flex-1 font-medium">{p.name}</span>
              {p.id === projectId && <Check className="size-5 text-accent" aria-label="Выбран" />}
            </button>
          ))}
        </Секция>

        <Секция title="Тема">
          <div className="flex gap-1 p-1.5" role="radiogroup" aria-label="Тема оформления">
            {ТЕМЫ.map(({ key, label, icon: Icon }) => (
              <button
                key={key} type="button" role="radio" aria-checked={тема === key}
                onClick={() => { setТема(key); saveTheme(key); }}
                className={`flex-1 min-h-11 rounded-xl flex flex-col items-center justify-center gap-0.5 text-xs font-semibold ${тема === key ? 'bg-accent-soft text-accent' : 'text-muted'}`}
              >
                <Icon className="size-5" aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </Секция>

        <Секция title="О приложении">
          <dl className="p-4 text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-muted">Версия</dt><dd className="text-right tnum">{APP_VERSION}</dd>
            <dt className="text-muted">Сборка</dt><dd className="text-right tnum">{BUILD_STAMP}</dd>
            <dt className="text-muted">Обновление</dt><dd className="text-right tnum break-all">{пакет}</dd>
            <dt className="text-muted">Платформа</dt><dd className="text-right">{platformName()}</dd>
          </dl>
        </Секция>

        <section className="px-4 pt-6">
          <button
            type="button" onClick={() => void logout()}
            className="w-full min-h-12 rounded-xl border border-line2 text-danger font-semibold flex items-center justify-center gap-2 active:bg-surface2"
          >
            <LogOut className="size-5" aria-hidden /> Выйти
          </button>
        </section>
      </div>
    </>
  );
}
